"use client";

import type { Transaction } from "@aera/data-table";

function writeHtmlToWindow(targetWindow: Window, html: string) {
  targetWindow.document.open();
  targetWindow.document.write(html);
  targetWindow.document.close();
  targetWindow.focus();
}

export function reserveTransactionsPrintViewTarget(params?: {
  title?: string;
}): Window | null {
  if (typeof window === "undefined") return null;

  try {
    const targetWindow = window.open("", "_blank");
    if (!targetWindow) return null;
    const title = params?.title ?? "กำลังเตรียมเอกสาร";
    writeHtmlToWindow(
      targetWindow,
      `<!doctype html><html><head><meta charset="utf-8"><title>${title}</title></head><body style="font-family: system-ui, -apple-system, sans-serif; padding: 24px;">กำลังเตรียมเอกสาร...</body></html>`,
    );
    return targetWindow;
  } catch {
    return null;
  }
}

export function openTransactionsPrintView(params: {
  actionLabel: string;
  schemaLabel?: string;
  rows: Transaction[];
  warning?: string;
  targetWindow?: Window | null;
  /** Replace the current tab (avoids popup blockers after async work, e.g. LINE external Safari). */
  reuseCurrentWindow?: boolean;
  /** When set, the "กลับหน้าหลัก" link navigates here. */
  returnUrl?: string;
}) {
  if (typeof window === "undefined") return;
  const {
    actionLabel,
    rows,
    schemaLabel,
    warning,
    targetWindow,
    reuseCurrentWindow,
    returnUrl,
  } = params;

  const unwrapCellValue = (v: unknown): unknown => {
    if (!v || typeof v !== "object") return v;
    const obj = v as Record<string, unknown>;
    if ("value" in obj) return obj.value;
    return v;
  };

  const cellLabelOverride = (v: unknown): string | null => {
    if (!v || typeof v !== "object") return null;
    const obj = v as Record<string, unknown>;
    const th = obj.th_name;
    return typeof th === "string" && th.trim() ? th.trim() : null;
  };

  const FIELD_LABELS_TH: Record<string, string> = {
    record_date: "วันที่รายการ",
    record_id: "รหัสรายการ",
    job_type: "ประเภท",
    amount: "จำนวนเงิน",
    transfer_amount: "ยอดโอน",
    total_amount: "ยอดรวม",
    slip_matched_amount: "จับคู่แล้ว",
    allocated_amount: "จับคู่แล้ว",
    unallocated_amount: "รอจัดสรร",
    party_name: "ผู้โอน/ลูกค้า",
    money_type: "ประเภทเงิน",
    transfer_date: "วันเวลาโอน",
    reference_no: "เลขอ้างอิง",
    sender_name: "ชื่อผู้โอน",
    sender_bank: "ธนาคาร (ผู้โอน)",
    recipient_account: "บัญชีรับเงิน",
    customer_name: "ชื่อลูกค้า",
    matched_receivable_customer: "ชื่อลูกค้าที่ยอดค้างจับคู่",
    recipient_name: "ชื่อผู้รับของ",
    phone: "เบอร์โทร",
    province: "จังหวัด",
    postcode: "รหัสไปรษณีย์",
    address: "ที่อยู่จัดส่ง",
    items: "รายการ",
    tracking_no: "เลขพัสดุ",
    slip_image_url: "ภาพสลิป",
    payment_channel: "ช่องทางชำระเงิน",
    vendor: "ร้านค้า",
    receipt_date: "วันที่ในใบเสร็จ",
    category: "หมวดหมู่",
    document_type: "ประเภทเอกสาร",
    tax_invoice: "ใบกำกับภาษี",
    seller_tax_id: "เลขผู้เสียภาษีผู้ขาย",
    vat: "ภาษีมูลค่าเพิ่ม (VAT)",
    status: "สถานะ",
    remark: "หมายเหตุ",
    image_url: "รูป/ไฟล์แนบ",
    recorded_at: "บันทึกเมื่อ",
    created_at: "สร้างเมื่อ",
  };
  const fieldLabel = (key: string) =>
    FIELD_LABELS_TH[key] ?? key.replaceAll("_", " ");

  const safe = (v: unknown) =>
    String(v ?? "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;");

  const safeAttr = (v: unknown) =>
    safe(v).replaceAll('"', "&quot;").replaceAll("'", "&#39;");

  const safeValue = (v: unknown) => {
    if (v === null || v === undefined) return "";
    const unwrapped = unwrapCellValue(v);
    if (unwrapped === null || unwrapped === undefined) return "";
    if (typeof unwrapped === "string") return safe(unwrapped);
    if (typeof unwrapped === "number" || typeof unwrapped === "boolean")
      return safe(unwrapped);
    try {
      return safe(JSON.stringify(unwrapped, null, 2));
    } catch {
      return safe(String(unwrapped));
    }
  };

  const formatMaybeIsoDate = (input: string) => {
    const s = input.trim();
    const hasTime = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(s);
    const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(s);
    if (!hasTime && !dateOnly) return input;
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return input;
    // Bangkok time, Thai Buddhist calendar — e.g. "3 ส.ค. 2569 16:42"
    if (hasTime) {
      return d.toLocaleString("th-TH", {
        timeZone: "Asia/Bangkok",
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    }
    return d.toLocaleDateString("th-TH", {
      timeZone: "Asia/Bangkok",
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const formatValueForKey = (key: string, v: unknown) => {
    const unwrapped = unwrapCellValue(v);
    if (typeof unwrapped === "string") {
      const DATE_KEYS = new Set([
        "record_date",
        "transfer_date",
        "receipt_date",
        "recorded_at",
        "created_at",
        "updated_at",
      ]);
      const maybeDate = formatMaybeIsoDate(unwrapped);
      if (maybeDate !== unwrapped) return maybeDate;
      // Force-format known date fields even if string shape is unusual.
      if (DATE_KEYS.has(key)) {
        const d = new Date(unwrapped);
        if (!Number.isNaN(d.getTime())) {
          return formatMaybeIsoDate(d.toISOString());
        }
      }
      if (key === "document_type") {
        const text = unwrapped.trim().toLowerCase();
        if (text === "invoice" || text === "ใบแจ้งหนี้") return "ใบแจ้งหนี้";
        if (text === "receipt" || text === "ใบเสร็จ") return "ใบเสร็จ";
      }
      return unwrapped;
    }
    if (typeof unwrapped === "number") {
      const isMoneyKey =
        key === "amount" ||
        key === "transfer_amount" ||
        key === "total_amount" ||
        key === "vat";
      if (isMoneyKey) {
        return unwrapped.toLocaleString("th-TH", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      }
      return unwrapped.toLocaleString("th-TH");
    }
    return unwrapped;
  };

  const ORDER: string[] = [
    "record_date",
    "record_id",
    "job_type",
    "amount",
    "payment_channel",
    "vendor",
    "receipt_date",
    "category",
    "tax_invoice",
    "seller_tax_id",
    "vat",
    "status",
    "remark",
    "image_url",
    "recorded_at",
    "created_at",
  ];

  const now = new Date();

  const recordRowsHtml = rows
    .map((r) => {
      const recRaw = r as Record<string, unknown>;
      const rec: Record<string, unknown> = Object.fromEntries(
        Object.entries(recRaw).map(([k, v]) => [k, unwrapCellValue(v)]),
      );
      const keys = Object.keys(rec).filter(
        (k) =>
          ![
            "workspace_id",
            "owner_user_id",
            "user_id",
            "id",
            "created_at",
            "recorded_at",
            // Internal link key — not useful on the printable summary.
            "payment_id",
          ].includes(k),
      );
      keys.sort((a, b) => {
        const ia = ORDER.indexOf(a);
        const ib = ORDER.indexOf(b);
        if (ia === -1 && ib === -1) return a.localeCompare(b);
        if (ia === -1) return 1;
        if (ib === -1) return -1;
        return ia - ib;
      });

      const title = String(rec.record_id ?? "").trim();
      const rawDate = String(
        rec.record_date ?? rec.transfer_date ?? "",
      ).trim();
      const subLeft = rawDate ? formatMaybeIsoDate(rawDate) : "";
      const subRight = String(
        rec.vendor ??
          rec.payment_channel ??
          rec.party_name ??
          rec.sender_name ??
          "",
      ).trim();

      const kvRows = keys
        .map(
          (k) => `<tr>
            <th class="kvK" title="${safe(k)}">${safe(
              cellLabelOverride(recRaw[k]) ?? fieldLabel(k),
            )}</th>
            <td class="kvV">${safeValue(formatValueForKey(k, recRaw[k]))}</td>
          </tr>`,
        )
        .join("");

      return `<section class="recordCard">
        <div class="recordHeader">
          <div class="recordTitle">${safe(title || actionLabel)}</div>
          <div class="recordMeta">
            <span>${safe(subLeft || "-")}</span>
            <span class="dot">•</span>
            <span>${safe(subRight || "-")}</span>
          </div>
        </div>
        <div class="kvWrap">
          <table class="kvTable" aria-label="รายละเอียดรายการ">
            <tbody>${kvRows}</tbody>
          </table>
        </div>
      </section>`;
    })
    .join("");

  const recordCount = rows.length;
  const warningHtml = warning
    ? `<div class="warn" role="note">${safe(warning)}</div>`
    : "";

  const html = `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${safe(actionLabel)}${schemaLabel ? ` — ${safe(schemaLabel)}` : ""}</title>
    <style>
      :root {
        color-scheme: light;
        --ink: #0f172a;
        --muted: #475569;
        --line: #fed7aa; /* orange-200 */
        --card: #ffffff;
        --soft: #fff7ed; /* orange-50 */
        --accentSoft: #ffedd5; /* orange-100 */
        --accentInk: #9a3412; /* orange-800 */
        --shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
      }
      * { box-sizing: border-box; }
      body {
        font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial, "Noto Sans Thai", "Noto Sans", sans-serif;
        margin: 0;
        color: var(--ink);
        background: radial-gradient(1200px 600px at 10% -10%, #ffedd5, transparent 60%),
                    radial-gradient(900px 500px at 95% 0%, #fde68a, transparent 55%),
                    #fff7ed;
      }
      .page { max-width: 1100px; margin: 22px auto; padding: 0 18px 28px; }
      .sheet { background: var(--card); border: 1px solid var(--line); border-radius: 16px; box-shadow: var(--shadow); overflow: hidden; }
      header { padding: 18px 18px 14px; border-bottom: 1px solid var(--line); background: linear-gradient(180deg, #ffffff, var(--soft)); }
      .titleRow { display:flex; align-items:flex-start; justify-content:space-between; gap:12px; }
      .brand { display:flex; align-items:center; gap:10px; min-width:0; }
      .logo { width:28px; height:28px; display:inline-flex; align-items:center; justify-content:center; flex: 0 0 auto; }
      .brandText { min-width: 0; }
      h1 { font-size: 18px; line-height: 1.2; margin: 0; letter-spacing: -0.01em; }
      .pill { display:inline-flex; align-items:center; gap:8px; padding:8px 10px; border:1px solid var(--line); border-radius:999px; font-size:12px; color: var(--muted); background:var(--soft); white-space:nowrap; }
      .pill b { color: var(--ink); font-weight: 800; }
      .meta { margin-top: 10px; display:grid; grid-template-columns: 1fr 1fr; gap:8px 12px; font-size:12px; color: var(--muted); }
      .meta div { padding:8px 10px; border:1px dashed var(--line); border-radius:12px; background:#fff; }
      .meta .k { font-size: 11px; color:#64748b; display:block; margin-bottom: 2px; }
      .meta .v { font-weight: 700; color: var(--ink); }
      .warn { margin: 12px 18px 0; padding: 10px 12px; border: 1px solid #fdba74; background: var(--soft); color: var(--accentInk); border-radius: 12px; font-size: 12px; font-weight: 600; }
      .content { padding: 16px 18px 18px; }
      .records { display: grid; gap: 12px; }
      .recordCard { border: 1px solid var(--line); border-radius: 14px; overflow: hidden; background: #fff; }
      .recordHeader { padding: 12px 12px 10px; border-bottom: 1px solid rgba(253, 186, 116, 0.45); background: linear-gradient(180deg,#fff,var(--soft)); }
      .recordTitle { font-weight: 900; letter-spacing: -0.01em; color: var(--ink); }
      .recordMeta { margin-top: 4px; font-size: 12px; color: var(--muted); display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
      .recordMeta .dot { opacity: .5; }
      .kvWrap { padding: 10px 12px 12px; }
      .kvTable { width: 100%; border-collapse: separate; border-spacing: 0; }
      .kvK, .kvV { padding: 9px 10px; border-bottom: 1px solid #eef2f7; vertical-align: top; font-size: 12px; }
      .kvK { width: 34%; text-align: left; color: #7c2d12; font-weight: 800; background: #fff; }
      .kvV { white-space: pre-wrap; word-break: break-word; }
      .kvTable tr:last-child .kvK, .kvTable tr:last-child .kvV { border-bottom: none; }
      footer { padding:12px 18px; border-top:1px solid var(--line); font-size:11px; color:#64748b; background:#fff; display:flex; justify-content:space-between; gap:10px; }
      .printBtn { appearance:none; border:1px solid var(--line); background:var(--accentSoft); color:var(--accentInk); border-radius:10px; padding:8px 10px; font-weight:700; font-size:12px; cursor:pointer; }
      .printBtn:hover { background:#fed7aa; }
      .backBtn { appearance:none; border:1px solid #e2e8f0; background:#fff; color:#334155; border-radius:10px; padding:8px 10px; font-weight:800; font-size:12px; cursor:pointer; }
      .backBtn:hover { background:#f8fafc; border-color:#cbd5e1; }
      .headerActions { display:flex; gap:10px; align-items:center; justify-content:flex-end; flex-wrap: wrap; }
      .headerActions .pill { margin: 0; }
      @media (max-width: 640px) { .meta { grid-template-columns: 1fr; } }
      @media print {
        .page { margin:0; padding:0; max-width:none; }
        .sheet { border:none; border-radius:0; box-shadow:none; }
        header, .content, footer { padding-left: 14mm; padding-right: 14mm; }
        .printBtn, .backBtn, .warn { display:none; }
        .recordCard { break-inside: avoid; page-break-inside: avoid; }
      }
    </style>
  </head>
  <body>
    <div class="page">
      <div class="sheet">
        <header>
          <div class="titleRow">
            <div class="brand">
              <div class="logo" aria-label="HireAera">
                <svg width="28" height="28" viewBox="0 0 96 96" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M50.2762 14.9033C62.4673 9.20388 75.6794 9.59616 84.1795 17.0703C96.3054 27.7328 94.0434 54.9322 79.3827 70.2198C65.3555 84.8466 45.7805 84.2641 33.494 74.8477C28.8431 79.7515 23.5557 83.2788 20.0077 83.7276C18.7613 84.1766 17.6112 84.4241 16.5985 84.4395C14.7887 84.8699 13.1802 84.9174 11.8583 84.5235C10.6291 84.4202 9.55634 84.0617 8.67759 83.4209C8.14033 83.1697 7.65141 82.8454 7.21568 82.4453C6.84167 82.1019 6.51498 81.7096 6.23521 81.2735C4.69092 79.6605 3.99702 77.1998 4.05552 74.167C3.27418 64.6517 10.7921 48.4116 23.2665 34.8272C32.2496 25.0446 42.1135 18.0004 50.2762 14.9033Z" fill="#0A0A0A"/>
                  <path d="M50.2904 0.146701C50.672 0.370426 50.9353 0.593176 51.1121 1.00758C51.2005 1.36372 51.2181 1.69189 51.2141 2.05809C51.2129 2.17215 51.2121 2.2862 51.2113 2.40371C51.2097 2.5266 51.2085 2.6495 51.2069 2.77612C51.2061 2.90541 51.2053 3.0347 51.2045 3.16398C51.2013 3.71048 51.1972 4.25696 51.1912 4.80344C51.1748 6.31584 51.1717 7.79272 51.4941 9.27912C51.6981 9.30048 51.6981 9.30048 51.8477 9.13132C53.5353 7.27868 54.7777 5.08008 56.0053 2.9121C56.3165 2.36327 56.6324 1.81773 56.9608 1.27906C57.0216 1.17944 57.0825 1.07983 57.1449 0.977192C57.4525 0.51896 57.8201 0.199144 58.3657 0.0568247C58.9253 0.0118676 59.2969 0.0599499 59.7973 0.342051C60.1241 0.657552 60.3576 0.946364 60.3936 1.4057C60.346 2.13325 59.9109 2.77341 59.5649 3.40228C59.5145 3.49394 59.4641 3.58558 59.4125 3.68001C59.0733 4.29348 58.7193 4.89588 58.3537 5.49396C57.4665 6.95236 56.6865 8.40812 56.1705 10.0397C56.1493 10.1071 56.1277 10.1744 56.1057 10.2438C55.7377 11.4474 55.4449 13.0479 55.9977 14.229C56.4753 14.973 57.1825 15.2362 58.0165 15.443C58.9849 15.64 59.9165 15.6899 60.9033 15.6882C61.1737 15.6878 61.4437 15.6891 61.7137 15.6909C62.3833 15.695 63.0524 15.696 63.722 15.6972C64.2912 15.6983 64.8609 15.7006 65.4301 15.7043C65.6953 15.7056 65.9605 15.7052 66.2257 15.7048C66.3885 15.7056 66.5513 15.7066 66.7141 15.7076C66.8233 15.7068 66.8233 15.7068 66.9349 15.7059C67.5137 15.7125 67.9625 15.8344 68.4169 16.2137C68.8517 16.7432 68.8257 17.2691 68.7689 17.931C68.6141 18.3847 68.4069 18.6211 68.0053 18.8817C67.5041 19.0946 67.0525 19.0978 66.5177 19.0935C66.3821 19.0935 66.382 19.0935 66.2436 19.0936C65.9464 19.0934 65.6489 19.0919 65.3517 19.0904C65.1449 19.0901 64.9381 19.0898 64.7313 19.0896C64.1881 19.0889 63.6449 19.087 63.1017 19.0849C62.5469 19.083 61.9925 19.0821 61.4377 19.0812C60.35 19.0791 59.2625 19.0759 58.1749 19.0719C59.2317 20.1318 60.4789 20.9425 61.7669 21.6965C62.0013 21.8343 62.2349 21.974 62.4681 22.1143C63.0965 22.4919 63.7253 22.8684 64.3593 23.2366C64.4317 23.2789 64.5045 23.3212 64.5793 23.3647C64.7145 23.4431 64.8497 23.5212 64.9853 23.5989C66.3165 24.3705 66.3165 24.3705 66.5737 25.0616C66.7285 25.6515 66.6881 26.1452 66.3829 26.6779C66.0901 27.0202 65.8877 27.2192 65.4281 27.2691C64.8993 27.283 64.5221 27.187 64.0625 26.9215C63.9957 26.8849 63.9293 26.8482 63.8605 26.8105C62.9833 26.3195 62.1285 25.7873 61.2697 25.2645C58.9385 23.8452 55.9213 22.058 53.0629 22.56C52.5261 22.6971 52.0717 22.8956 51.7357 23.3492C50.9401 24.7867 50.9109 26.4824 50.8945 28.0784C50.8929 28.2118 50.8909 28.3452 50.8893 28.4786C50.8837 28.8985 50.8784 29.3183 50.8736 29.7381C50.868 30.224 50.8621 30.7099 50.8553 31.1957C50.8529 31.3872 50.8509 31.5786 50.8489 31.77C50.8405 32.4051 50.8245 33.0398 50.7945 33.6742C50.7893 33.7956 50.7837 33.9169 50.7785 34.042C50.7101 34.5232 50.5637 34.8393 50.2057 35.1693C49.6929 35.5141 49.1328 35.478 48.5352 35.4248C48.1216 35.3101 47.9053 35.0996 47.6609 34.746C47.3637 34.1207 47.4369 33.4136 47.4449 32.7374C47.4453 32.5854 47.4457 32.4333 47.4457 32.2812C47.4465 31.9624 47.4485 31.6438 47.4513 31.3251C47.4553 30.9224 47.4568 30.5196 47.4576 30.1169C47.4616 28.5251 47.4721 26.9356 47.5809 25.3468C47.2605 25.605 46.9653 25.844 46.7145 26.1721C46.6653 26.236 46.6161 26.3 46.5653 26.3659C46.5149 26.4336 46.4641 26.5012 46.4117 26.5709C46.3577 26.6428 46.3032 26.7146 46.2472 26.7886C44.6868 28.8709 43.4241 31.1528 42.1377 33.4085C41.7497 34.0879 41.3568 34.7643 40.9636 35.4407C40.6432 35.9929 40.3253 36.5465 40.0105 37.102C39.9071 37.2841 39.8036 37.4661 39.7001 37.6482C39.634 37.7648 39.5681 37.8816 39.5024 37.9985C38.5712 39.6554 38.5713 39.6554 37.7684 39.9587C37.0826 40.0564 36.6102 39.9966 36.0326 39.6081C35.6909 39.2458 35.5916 38.85 35.6019 38.3532C35.7153 37.7356 36.0983 37.1959 36.4143 36.6608C36.5137 36.49 36.613 36.3192 36.7122 36.1483C36.937 35.7622 37.1631 35.377 37.39 34.9922C37.752 34.3776 38.1095 33.7606 38.4663 33.143C38.8736 32.4384 39.2825 31.7347 39.6949 31.033C42.3057 26.8767 42.3057 26.8767 43.3161 22.1559C43.3153 22.086 43.3149 22.0162 43.3145 21.9442C43.2993 21.2342 43.1837 20.593 42.6765 20.0549C41.1445 18.7873 38.6954 19.1027 36.8356 19.0711C36.5766 19.0666 36.3176 19.0611 36.0586 19.0546C35.8167 19.0487 35.5748 19.0442 35.3329 19.0416C34.6672 19.0243 34.2093 18.9613 33.6465 18.5965C33.3007 18.1831 33.1446 17.7084 33.1693 17.1704C33.2835 16.5799 33.5358 16.268 34.0283 15.9344C34.3942 15.7584 34.6868 15.7201 35.0916 15.7184C35.208 15.7175 35.3244 15.7165 35.4443 15.7155C35.5698 15.7153 35.6953 15.7151 35.8245 15.7149C35.9565 15.7138 36.0884 15.7127 36.2203 15.7114C36.778 15.7062 37.3357 15.7032 37.8934 15.7008C38.3014 15.699 38.7093 15.6958 39.1172 15.6911C39.1831 15.6904 39.2489 15.6896 39.3168 15.6889C40.3733 15.6746 41.3929 15.5824 42.4273 15.364C42.4273 15.0452 42.168 14.8876 41.952 14.6693C41.8844 14.6024 41.8168 14.5356 41.7472 14.4667C41.6776 14.3981 41.6085 14.3295 41.5373 14.2588C40.9165 13.6532 40.2629 13.1188 39.5639 12.6068C39.4639 12.5314 39.3638 12.456 39.2608 12.3784C38.3322 11.6886 37.3376 11.1048 36.3451 10.5122C36.0162 10.3151 35.6883 10.1164 35.3607 9.91716C34.3212 9.28512 33.2805 8.65628 32.2238 8.05296C31.488 7.63188 30.7611 7.1962 30.0349 6.75904C29.5821 6.4868 29.129 6.21656 28.6683 5.9578C28.5945 5.916 28.5207 5.87424 28.4447 5.8312C28.3118 5.75636 28.1784 5.68252 28.0442 5.60996C27.5442 5.3272 27.1305 4.94872 26.8702 4.43028C26.7556 3.17307 26.7557 3.17307 27.0671 2.7011C27.3395 2.40007 27.6211 2.25504 28.0155 2.14848C29.3227 2.10358 30.7443 3.33657 31.816 3.98128C32.301 4.27288 32.7878 4.56112 33.2767 4.84624C33.7575 5.12688 34.2369 5.40964 34.7143 5.69596C35.5721 6.21012 36.4337 6.71784 37.2966 7.22348C37.4675 7.32368 37.6384 7.42392 37.8092 7.52432C38.3278 7.82876 38.8484 8.1294 39.373 8.42344C39.4309 8.456 39.4888 8.4886 39.5485 8.52216C41.2925 9.48828 43.8193 10.4635 45.8153 9.91492C46.1385 9.81348 46.3577 9.67196 46.6265 9.46928C46.6897 9.46928 46.7525 9.46928 46.8173 9.46928C46.8589 9.38508 46.8588 9.38508 46.9012 9.29916C46.9928 9.11916 47.0925 8.94312 47.1933 8.76808C47.9941 7.15784 47.7925 5.06888 47.8173 3.31758C47.8213 3.06169 47.8261 2.80583 47.8309 2.54995C47.8317 2.47308 47.8325 2.39622 47.8337 2.31702C47.8469 1.6918 47.8873 1.02444 48.3325 0.544084C48.3797 0.491384 48.4264 0.438684 48.4748 0.384389C48.9736 -0.0554313 49.6812 -0.0933181 50.2904 0.146701Z" fill="#F59E0B"/>
                </svg>
              </div>
              <div class="brandText"><h1>${safe(actionLabel)}</h1></div>
            </div>
            <div class="headerActions">
              <a id="aeraBackLink" class="backBtn" href="${safeAttr(
                returnUrl ?? "",
              )}">กลับหน้าหลัก</a>
              <div class="pill">จำนวนรายการ <b>${safe(recordCount.toLocaleString("th-TH"))}</b></div>
            </div>
          </div>
          <div class="meta">
            <div><span class="k">พิมพ์เมื่อ</span><span class="v">${safe(now.toLocaleString("th-TH"))}</span></div>
            <div><span class="k">ชุดข้อมูล</span><span class="v">${safe(schemaLabel ?? "-")}</span></div>
          </div>
        </header>
        ${warningHtml}
        <div class="content">
          <div class="records" aria-label="รายการธุรกรรม">
            ${recordRowsHtml || `<div style="padding:16px;border:1px solid var(--line);border-radius:14px;background:#fff;color:#64748b;font-weight:700;">ไม่มีข้อมูลที่จะแสดง</div>`}
          </div>
        </div>
        <footer>
          <div class="hint">เคล็ดลับ: กด “พิมพ์/บันทึก PDF” แล้วเลือก “Save as PDF”</div>
          <div style="display:flex; gap:10px; align-items:center;">
            <button class="printBtn" type="button" onclick="window.print()">พิมพ์/บันทึก PDF</button>
            <div>Generated by HireAera</div>
          </div>
        </footer>
      </div>
    </div>
    <script>
      (function(){
        try { window.focus(); } catch (e) {}
        var returnUrl = ${JSON.stringify(returnUrl ?? "")};
        var link = document.getElementById("aeraBackLink");
        try {
          if (link && (!returnUrl || returnUrl === "")) {
            if (document.referrer) link.setAttribute("href", document.referrer);
            else link.setAttribute("href", "/");
          }
        } catch (e) {}
      })();
    </script>
  </body>
</html>`;

  const openBlobInNewTab = () => {
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  if (reuseCurrentWindow) {
    writeHtmlToWindow(window, html);
    return;
  }

  if (targetWindow && !targetWindow.closed) {
    writeHtmlToWindow(targetWindow, html);
    return;
  }

  openBlobInNewTab();
}

